import { Box, Container, Grid, Typography, Link, IconButton, Divider, Paper } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import CloudIcon from '@mui/icons-material/Cloud';
import ForestIcon from '@mui/icons-material/Forest';
import TerrainIcon from '@mui/icons-material/Terrain';
import ExploreIcon from '@mui/icons-material/Explore';

const Footer = () => {
  // Let the theme handle footer styling completely
  
  const MainIcon = ForestIcon;
  const iconColor = 'secondary.main';
  
  const paperStyles = {
    backgroundColor: 'rgba(255, 251, 242, 0.1)',
    border: '1px solid',
    borderColor: 'secondary.dark'
  };
  
  const dividerStyle = { borderColor: 'rgba(217, 165, 102, 0.3)' };
  const linkHoverColor = 'secondary.main';

  return (
    <Box
      sx={{
        py: 6,
        width: '100%',
        marginTop: 'auto',
        position: 'relative',
      }}
      component="footer"
      role="contentinfo"
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <MainIcon sx={{ mr: 1, color: iconColor }} aria-hidden="true" />
              <Typography 
                variant="h6" 
                component="span"
                role="heading"
                aria-level={3}
                sx={{ 
                  fontWeight: 700,
                  letterSpacing: '.1rem',
                  textTransform: 'uppercase'
                }}
              >
                BEGARD GEAR
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'background.paper', opacity: 0.9 }}>
              Specializing in clothing to prevent bee stings. 
              Quality gear for outdoor enthusiasts and adventurers.
            </Typography>
            <Box 
              component={Paper} 
              elevation={0} 
              sx={{ 
                mt: 2, 
                p: 2, 
                ...paperStyles,
                borderRadius: 1
              }}
              role="presentation"
            >
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'background.paper' }}>
                "Into the wild with confidence."
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ExploreIcon sx={{ mr: 1, color: iconColor }} aria-hidden="true" />
              <Typography 
                variant="h6" 
                component="h2"
                sx={{ 
                  fontWeight: 700,
                  letterSpacing: '.05rem'
                }}
              >
                EXPLORE
              </Typography>
            </Box>
            <nav aria-label="Footer navigation">
              <Link 
                component={RouterLink} 
                to="/" 
                color="inherit" 
                sx={{ 
                  display: 'block', 
                  mb: 1.5, 
                  '&:hover': { color: linkHoverColor },
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:focus': {
                    outline: '2px solid',
                    outlineOffset: 2,
                  }
                }}
                aria-label="Return to homepage"
              >
                Home
              </Link>
              <Link 
                component={RouterLink} 
                to="/products" 
                color="inherit" 
                sx={{ 
                  display: 'block', 
                  mb: 1.5, 
                  '&:hover': { color: linkHoverColor },
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:focus': {
                    outline: '2px solid',
                    outlineOffset: 2,
                  }
                }}
                aria-label="Browse our products"
              >
                Products
              </Link>
              <Link 
                component={RouterLink} 
                to="/about" 
                color="inherit" 
                sx={{ 
                  display: 'block', 
                  mb: 1.5, 
                  '&:hover': { color: linkHoverColor },
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:focus': {
                    outline: '2px solid',
                    outlineOffset: 2,
                  }
                }}
                aria-label="Learn about Begard Gear"
              >
                About Us
              </Link>
              <Link 
                component={RouterLink} 
                to="/contact" 
                color="inherit" 
                sx={{ 
                  display: 'block', 
                  mb: 1.5, 
                  '&:hover': { color: linkHoverColor },
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:focus': {
                    outline: '2px solid',
                    outlineOffset: 2,
                  }
                }}
                aria-label="Get in touch with us"
              >
                Contact
              </Link>
              <Link 
                component={RouterLink} 
                to="/disclaimer" 
                color="inherit" 
                sx={{ 
                  display: 'block', 
                  mb: 1.5, 
                  '&:hover': { color: linkHoverColor },
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:focus': {
                    outline: '2px solid',
                    outlineOffset: 2,
                  }
                }}
                aria-label="Read our disclaimer"
              >
                Disclaimer
              </Link>
            </nav>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TerrainIcon sx={{ mr: 1, color: iconColor }} aria-hidden="true" />
              <Typography 
                variant="h6" 
                component="h2"
                sx={{ 
                  fontWeight: 700,
                  letterSpacing: '.05rem'
                }}
              >
                CONNECT
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton 
                href="https://facebook.com/begardgear" 
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit our Facebook page"
                sx={{ 
                  color: 'inherit',
                  '&:hover': { color: linkHoverColor },
                  '&:focus': {
                    outline: '2px solid',
                    outlineOffset: 2,
                  }
                }}
              >
                <FacebookIcon />
              </IconButton>
              <IconButton 
                href="https://instagram.com/begardgear" 
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit our Instagram page"
                sx={{ 
                  color: 'inherit',
                  '&:hover': { color: linkHoverColor },
                  '&:focus': {
                    outline: '2px solid',
                    outlineOffset: 2,
                  }
                }}
              >
                <InstagramIcon />
              </IconButton>
              <IconButton 
                href="https://bsky.app/profile/begardgear.bsky.social" 
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit our Bluesky profile"
                sx={{ 
                  color: 'inherit',
                  '&:hover': { color: linkHoverColor },
                  '&:focus': {
                    outline: '2px solid',
                    outlineOffset: 2,
                  }
                }}
              >
                <CloudIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, ...dividerStyle }} role="presentation" />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            © {new Date().getFullYear()} Begard Gear. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 